// src/components/Header.jsx
//
// Restyled to match CET-HTML-Code/dashboard.html's topbar (the
// "app-topbar" markup) -- same brand logo, search box, notification
// bell, and user profile dropdown as before, just using the theme's
// own classes/icons (Tabler `ti ti-*` icons, loaded via app.min.css)
// instead of the app's old hand-rolled topbar-* classes.
//
// Interactivity (both dropdowns, the sidebar collapse toggle) is
// handled with plain React state rather than the theme's own
// vendors.min.js/app.js -- see the note in public/index.html for why.
// The theme's own CSS classes (.dropdown-menu, .show, etc.) are reused
// so it looks identical either way.
//
// One thing that ISN'T identical without the theme's JS: Bootstrap's
// compiled CSS only positions `.dropdown-menu` below its toggle button
// once Popper.js (loaded by Bootstrap's own JS) stamps a
// `data-bs-popper` attribute onto it -- see the theme's own
// `.dropdown-menu[data-bs-popper]{top:100%;left:0;...}` rule. Without
// that attribute, `.dropdown-menu.show` just falls back to Bootstrap's
// base `position:absolute` with no offset, which places it at its
// normal position in the page flow instead of dropping below the
// button -- in this topbar that meant the first couple of items
// ("Welcome back!" / "Profile") rendered underneath the header bar
// itself and were invisible, while the rest of the menu spilled out
// below it. Both dropdown-menus below set `top`/`right` inline to
// reproduce what `[data-bs-popper]` would have done.
//
// User info (name / role / avatar) is passed in as props -- Layout.jsx
// supplies it from localStorage("currentUser"), unchanged from before.
// Notifications are different: this component fetches its own real data
// from GET /notifications now (see loadNotifications below) instead of
// trusting a hardcoded notificationCount prop.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api as axios, API_ORIGIN } from "../config/apiConfig";
import { clearWizardDraft } from "../utils/wizardDraft";
import { isModuleVisible } from "../utils/permissions";
import { ROUTES, editUserPath } from "../config/routes";

// Notifications used to be grouped into category sections ("Cost
// Estimation", "Access Request", ...) here via groupNotificationsByCategory
// + NOTIFICATION_CATEGORY_ORDER. Per request that grouping was removed --
// a fixed category order meant a brand new notification (e.g. a just-
// submitted Help message) could land in a section near the bottom instead
// of showing at the top, even though it was the most recent one overall.
// The notifications list below is rendered flat now, newest first (the
// API already returns it that way), with n.category only used per-row for
// its icon (see getNotificationIcon).

// One emoji per notification "type" -- for Access Request / User
// Management, which don't show a per-notification actor avatar (see
// isCostEstimationNotification below, which covers both Started and
// Completed Cost Estimation events instead). Falls back to no icon for
// anything unrecognized rather than guessing.
// Per request, Access Request / User Management / the default fallback
// now render the same branded icon images used on the full Notifications
// page (public/assets/images/logo/Access Request.png / New User.png /
// Defuult User.png) instead of a plain emoji; Help keeps its emoji since
// no icon was supplied for it. Sized/aligned to sit inline with the
// message text the same way the emoji used to.
function getNotificationIcon(n) {
  if (n.category === "Access Request") {
    return (
      <img
        src={encodeURI("/assets/images/logo/Access Request.png")}
        alt="Access Request"
        style={{ width: 18, height: 18, verticalAlign: "middle" }}
      />
    );
  }
  if (n.category === "User Management") {
    return (
      <img
        src={encodeURI("/assets/images/logo/New User.png")}
        alt="New User"
        style={{ width: 18, height: 18, verticalAlign: "middle" }}
      />
    );
  }
  // "Help" -- per request, a Help Center "Send Us a Message" submission
  // (submit_help_message in app.py, fanned out to every Super Admin the
  // same way Access Request/User Management are) shows up here too.
  // Per a later request, this now uses the System Icon.png image instead
  // of the plain speech-bubble emoji.
  if (n.category === "Help") {
    return (
      <img
        src={encodeURI("/assets/images/logo/System Icon.png")}
        alt="Help"
        style={{ width: 18, height: 18, verticalAlign: "middle" }}
      />
    );
  }
  return (
    <img
      src={encodeURI("/assets/images/logo/Defuult User.png")}
      alt="User"
      style={{ width: 18, height: 18, verticalAlign: "middle" }}
    />
  );
}

// True for any Cost Estimation notification (both "Started" and
// "Completed") -- these show the actor's real avatar (NotificationAvatar
// below) instead of an emoji from getNotificationIcon. Originally this
// was Started-only with Completed getting a plain ✅, but per request
// Completed now shows the real photo too, same as Started.
function isCostEstimationNotification(n) {
  return n.category === "Cost Estimation";
}

// Renders the acting person's actual profile photo (actorProfileImage --
// same filename Users.ProfileImage stores, served via the existing
// GET /profile_photo/<filename>, see notifications_actor_photo_column.sql)
// next to a Cost Estimation notification (Started or Completed), matching
// the real circular avatar photo shown elsewhere in the app (e.g. the
// user menu button below) instead of a generic person emoji. Falls back
// to an initials bubble -- same idea as the user menu's own avatar/
// initials fallback further down this file -- when there's no photo yet,
// or the image fails to load.
function NotificationAvatar({ actorName, actorProfileImage }) {
  const [imgError, setImgError] = useState(false);
  const initial = (actorName || "?").trim().charAt(0).toUpperCase() || "?";
  if (actorProfileImage && !imgError) {
    return (
      <img
        src={`${API_ORIGIN}/profile_photo/${actorProfileImage}`}
        alt={actorName || "User"}
        className="rounded-circle"
        style={{ width: 20, height: 20, objectFit: "cover", verticalAlign: "middle" }}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <span
      className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center fw-semibold"
      style={{ width: 20, height: 20, fontSize: 10, verticalAlign: "middle" }}
    >
      {initial}
    </span>
  );
}

// Date + time ("27 Aug 2026 – 22:05") for the actor/date header line
// above each notification's message. Was date-only -- per request, now
// matches the same "day Mon year – HH:MM" format the full Notifications
// page's Date Received column already uses.
function formatNotificationDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} – ${hh}:${mm}`;
}

export default function Header({
  userName = "Guest",
  userRole = "",
  userAvatarUrl = "",
  notificationCount = 0
}) {
  // notificationCount above is now just a fallback used before the real
  // fetch below finishes (was previously the ONLY source, always
  // hardcoded to 0 by Layout.jsx -- see notifications and unreadCount
  // state instead, populated from GET /notifications, the same endpoint
  // Notifications.jsx uses).
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(notificationCount);
  // The old topbar hamburger button (and its sidebarCondensed/
  // onToggleSidebar props) that used to live here is gone -- the
  // sidebar now has its own pin/unpin control (the circle icon at the
  // top of SideMenu.jsx, matching the theme's .button-on-hover exactly,
  // per the reference recording). Under the on-hover/on-hover-active
  // attribute values that control now uses, app.min.css hides
  // .sidenav-toggle-button unconditionally anyway (that's the theme's
  // own design -- the two controls are meant to be alternatives, not
  // both present at once), so keeping it here would just be dead,
  // permanently-invisible markup. See Layout.jsx for where the sidebar
  // state now lives.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);
  const exploreRef = useRef(null);
  const navigate = useNavigate();

  // ---- Quick Search -----------------------------------------------------
  // The header search box used to be pure decoration (no state, no
  // handler at all). Now: type 2+ characters, debounce 300ms, hit
  // GET /quick-search?q= (new endpoint in app.py) for a grouped
  // Projects / Rate Card items / Users result set, and show it as a
  // dropdown under the box -- same manual-state dropdown pattern as
  // Explore/Notifications/Profile above (no Bootstrap JS loaded here).
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ projects: [], rate_card: [], users: [], sub_elements: [] });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    const q = searchQuery.trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (q.length < 2) {
      setSearchResults({ projects: [], rate_card: [], users: [], sub_elements: [] });
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(() => {
      axios
        .get("/quick-search", { params: { q } })
        .then((res) => {
          setSearchResults(res.data?.data || { projects: [], rate_card: [], users: [], sub_elements: [] });
        })
        .catch((err) => {
          console.error("Quick search failed:", err);
          setSearchResults({ projects: [], rate_card: [], users: [], sub_elements: [] });
        })
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults({ projects: [], rate_card: [], users: [], sub_elements: [] });
  };

  const goToProject = (p) => {
    closeSearch();
    navigate(`${ROUTES.PROJECT}?q=${encodeURIComponent(p.projectid || "")}&status=${encodeURIComponent(p.status || "")}`);
  };

  const goToRateCardItem = (rc) => {
    closeSearch();
    // Whatever text actually matched is what the Rate Card page's own
    // search box should be prefilled with -- Short_Description is the
    // most reliably-populated/most specific field on a Rate_Card row.
    const term = rc.Short_Description || rc.Asset_Element || rc.Asset_Group || String(rc.RC_ID);
    navigate(`${ROUTES.RATE_CARD}?q=${encodeURIComponent(term)}`);
  };

  const goToUser = (u) => {
    closeSearch();
    navigate(editUserPath(u.id));
  };

  // A "sub_elements" hit is a SAVED Step 4 cost item (project_sub_elements,
  // matched on its Short Description), not the Rate Card master catalog --
  // see the comment on the backend quick_search() query. Per request, this
  // must open Step 4 itself, not a "project card"/Step 1 landing page.
  //
  // Step4SubElement.jsx only ever reads two things from localStorage:
  // "step3" (projectName/projectID/C_ID/rows) and the one-shot
  // "step4QuickSearchTerm" below -- it has no dependency on "step1" at
  // all (confirmed: nothing in that file reads it). So rather than
  // routing through Step 1 and making the user click Next three times,
  // this fetches the project's already-saved Asset Selection directly
  // (GET /get_project_asset_selection -- the exact same endpoint
  // Step3AssetSelection.jsx itself calls to prefill from the database)
  // and rebuilds the "step3" draft from it in the same shape
  // Step3AssetSelection.jsx's own save writes: one "row" per saved Asset
  // Group, each with a single-entry assetElementsData array holding that
  // row's Asset Element + its checked Main Elements. "step1" is still
  // set the same way ProjectList.jsx's handleEdit() does, purely so
  // clicking Back from Step 4 (which lands on Step 3, and Step 3 DOES
  // need "step1") doesn't hit an empty draft.
  //
  // A project can only reach Step 4 after Step 3 has saved an Asset
  // Selection, so get_project_asset_selection returning nothing here
  // would mean the data changed out from under us between the search
  // result loading and the click -- falls back to the old "open Step 1"
  // behavior in that edge case rather than leaving the user stuck.
  const goToSubElement = async (se) => {
    closeSearch();
    const step1Data = {
      projectName: se.project_name || "",
      projectID: se.projectid || "",
      estimatedBy: se.estimated_by || "",
      date: se.estimation_date ? String(se.estimation_date).substring(0, 10) : "",
      opco: se.opco_id != null ? String(se.opco_id) : "",
      class: se.class || "",
      C_ID: se.c_id || "",
      duration: se.duration || "",
      intro: se.intro || "",
      inflation: se.inflation || "",
      financial: se.financial || "",
      contingency: se.contingency || "",
      risks: se.risks || "",
      opportunities: se.opportunities || "",
    };
    localStorage.setItem("step1", JSON.stringify(step1Data));
    localStorage.setItem("projectID", se.projectid);
    localStorage.setItem("project_db_id", se.id);
    localStorage.setItem("step4QuickSearchTerm", se.description || se.sub_element || "");

    try {
      const res = await axios.get("/get_project_asset_selection", { params: { projectId: se.projectid } });
      const d = res.data?.data;
      const savedRows = d?.rows || [];
      if (!res.data?.success || !d || savedRows.length === 0) {
        throw new Error("No saved Asset Selection for this project");
      }

      const step3Data = {
        projectID: se.projectid || "",
        projectName: se.project_name || "",
        operatingCompany: "",
        C_ID: se.c_id || "",
        dataYears: d.dataYear ? [d.dataYear] : [],
        rows: savedRows.map((r) => ({
          assetGroupID: r.assetGroupID,
          assetGroupName: r.assetGroupName,
          assetElementsData: [
            {
              assetElementID: r.assetElementID,
              assetElementName: r.assetElementName,
              mainElements: r.mainElements || [],
            },
          ],
        })),
      };
      localStorage.setItem("step3", JSON.stringify(step3Data));
      navigate(ROUTES.COST_ESTIMATION_STEP4);
    } catch (err) {
      console.error("Quick Search: failed to jump straight to Step 4, falling back to Step 1:", err);
      navigate(ROUTES.COST_ESTIMATION_STEP1);
    }
  };

  const searchHasQuery = searchQuery.trim().length >= 2;
  const searchTotal =
    searchResults.projects.length +
    searchResults.rate_card.length +
    searchResults.users.length +
    searchResults.sub_elements.length;

  // Real notifications for the bell -- top 5 most recent (any status),
  // fetched once on mount and again whenever the dropdown is opened, so
  // it reflects anything new (e.g. a cost estimation finished in another
  // tab) without needing a page refresh.
  const loadNotifications = useCallback(async () => {
    try {
      const res = await axios.get("/notifications", { params: { status: "all" } });
      const list = res.data?.notifications || [];
      setNotifications(list.slice(0, 5));
      setUnreadCount(typeof res.data?.unreadCount === "number" ? res.data.unreadCount : 0);
    } catch (err) {
      // Quiet failure -- a broken notifications fetch shouldn't break
      // the rest of the header (search, Explore, profile menu all still
      // need to work). Table not existing yet (before
      // help_and_notifications_tables.sql has been run) lands here too.
      console.error("Failed to load notifications:", err);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (notifOpen) loadNotifications();
  }, [notifOpen, loadNotifications]);

  // Explore mega-menu's "Support" / "Helpline" strip at the bottom used
  // to be hardcoded (info@dashboard.com / +966-123456789). Per request,
  // it now reads from the same Contact_Info table/GET /contact-info
  // endpoint Help.jsx's "Get in Touch" panel already uses (see
  // contact_info_table.sql) -- just the first email and first contact
  // number, since this strip only has room for one of each (Help.jsx
  // still lists every number/email it returns). Fetched once on mount;
  // no need to refetch on every Explore open since this data changes
  // rarely and there's no edit screen for it yet.
  const [contactInfo, setContactInfo] = useState(null);
  useEffect(() => {
    axios
      .get("/contact-info")
      .then((res) => {
        if (res.data?.success) setContactInfo(res.data.data);
      })
      .catch((err) => {
        // Quiet failure -- falls back to the hardcoded defaults below,
        // same reasoning as the notifications fetch above.
        console.error("Failed to load contact info:", err);
      });
  }, []);
  const supportEmail = contactInfo?.emailIds?.[0] || "info@dashboard.com";
  const helplineNumber = contactInfo?.contactNumbers?.[0] || "+966-123456789";

  useEffect(() => {
    if (!userMenuOpen && !notifOpen && !exploreOpen && !searchOpen) return;
    const handleClickOutside = (e) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (exploreOpen && exploreRef.current && !exploreRef.current.contains(e.target)) {
        setExploreOpen(false);
      }
      if (searchOpen && searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen, notifOpen, exploreOpen, searchOpen]);

  // Explore mega-menu items that go somewhere real vs. ones that don't
  // have a page yet. Master data management and system Configuration
  // aren't built out in this app yet (see the note on Category B/C pages
  // from the original restyle plan), so rather than link them to a route
  // that doesn't exist and silently render a blank page, they're shown
  // dimmed/inert with a "coming soon" title -- same honesty rule already
  // applied to Rate Card's row-action icons.
  const goExplore = (path) => {
    setExploreOpen(false);
    navigate(path);
  };

  // Both "Cost Estimation" quick links below (the Explore dropdown item
  // and the promo banner's "Start Estimation" button) start a brand-new
  // estimation, same as ProjectList.jsx's "Start Cost Estimation" button
  // -- so they need the same wizard-draft reset first, or a stale budget/
  // asset-selection/etc. from whatever was last worked on would silently
  // reappear on steps 2+ of the "new" one. See src/utils/wizardDraft.js.
  const goStartNewEstimation = () => {
    clearWizardDraft();
    goExplore(ROUTES.COST_ESTIMATION_STEP1);
  };

  const initials = userName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="app-topbar">
      <div className="container-fluid topbar-menu">
        <div className="d-flex align-items-center gap-2">
          <div className="logo-topbar">
            <Link to={ROUTES.DASHBOARD} className="logo-light">
              {/* New brand assets (per request). The full wordmark's dark
                  navy/gray text has poor contrast against this bar's dark
                  green background, so both slots use the icon-only mark
                  here -- the full logo (with text) is reserved for
                  light-background pages like Login. */}
              {/* Explicit height/width:auto -- the source PNG's native
                  size (214x239) is much bigger than this topbar slot and
                  nothing in the theme's CSS constrains it, so without
                  this it rendered oversized. Same fix as SideMenu.jsx. */}
              <span className="logo-lg">
                <img src="/assets/images/logo/CET-Full-icon.png" alt="Cost Estimation Tool" style={{ height: 36, width: "auto" }} />
              </span>
              <span className="logo-sm">
                <img src="/assets/images/logo/CET-Full-icon.png" alt="Cost Estimation Tool" style={{ height: 30, width: "auto" }} />
              </span>
            </Link>
          </div>

          <div className="app-search d-none d-xl-flex" ref={searchRef}>
            <input
              type="search"
              className="form-control rounded-pill topbar-search"
              placeholder="Quick Search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => {
                if (searchHasQuery) setSearchOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") closeSearch();
              }}
            />
            <i className="ti ti-search app-search-icon text-muted" />

            {searchOpen && searchHasQuery && (
              <div
                className="dropdown-menu p-0 show"
                style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "0.5rem", maxHeight: 420, overflowY: "auto" }}
              >
                {searchLoading ? (
                  <div className="dropdown-item text-wrap py-2 text-muted">Searching...</div>
                ) : searchTotal === 0 ? (
                  <div className="dropdown-item text-wrap py-2 text-muted">No results for "{searchQuery.trim()}"</div>
                ) : (
                  <>
                    {searchResults.projects.length > 0 && (
                      <>
                        <div className="px-3 py-1 fs-11 text-uppercase fw-semibold text-muted bg-light">Projects</div>
                        {searchResults.projects.map((p) => (
                          <button
                            key={`p-${p.id}`}
                            type="button"
                            className="dropdown-item text-start w-100 border-0 bg-transparent py-2"
                            onClick={() => goToProject(p)}
                          >
                            <div className="fw-semibold">{p.projectid} — {p.project_name}</div>
                            <div className="fs-12 text-muted">{p.opco_name} • {p.class}</div>
                          </button>
                        ))}
                      </>
                    )}

                    {searchResults.rate_card.length > 0 && (
                      <>
                        <div className="px-3 py-1 fs-11 text-uppercase fw-semibold text-muted bg-light">Rate Card Items</div>
                        {searchResults.rate_card.map((rc) => (
                          <button
                            key={`rc-${rc.RC_ID}`}
                            type="button"
                            className="dropdown-item text-start w-100 border-0 bg-transparent py-2"
                            onClick={() => goToRateCardItem(rc)}
                          >
                            <div className="fw-semibold">{rc.Short_Description || `${rc.Asset_Group} / ${rc.Asset_Element}`}</div>
                            <div className="fs-12 text-muted">
                              {rc.Main_Element}{rc.Sub_Element ? ` / ${rc.Sub_Element}` : ""} • Rate {rc.Rate ?? "-"} • {rc.RateYear ?? "-"}
                            </div>
                          </button>
                        ))}
                      </>
                    )}

                    {searchResults.sub_elements.length > 0 && (
                      <>
                        <div className="px-3 py-1 fs-11 text-uppercase fw-semibold text-muted bg-light">Sub Elements (Step 4)</div>
                        {searchResults.sub_elements.map((se, idx) => (
                          <button
                            key={`se-${se.projectid}-${idx}`}
                            type="button"
                            className="dropdown-item text-start w-100 border-0 bg-transparent py-2"
                            onClick={() => goToSubElement(se)}
                          >
                            <div className="fw-semibold">{se.description || se.sub_element}</div>
                            <div className="fs-12 text-muted">
                              {se.projectid} — {se.project_name}
                              {se.main_element ? ` • ${se.main_element}` : ""}
                            </div>
                          </button>
                        ))}
                      </>
                    )}

                    {searchResults.users.length > 0 && (
                      <>
                        <div className="px-3 py-1 fs-11 text-uppercase fw-semibold text-muted bg-light">Users</div>
                        {searchResults.users.map((u) => (
                          <button
                            key={`u-${u.id}`}
                            type="button"
                            className="dropdown-item text-start w-100 border-0 bg-transparent py-2"
                            onClick={() => goToUser(u)}
                          >
                            <div className="fw-semibold">{u.UserName}</div>
                            <div className="fs-12 text-muted">{u.Email} • {u.RoleName || "No role"}</div>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Explore mega-menu -- matches the theme's #megamenu-apps. Same
              manual-state + inline-positioning pattern as the notification
              and user dropdowns below (no Bootstrap JS loaded, see the
              file-level comment on the data-bs-popper bug). */}
          <div className="topbar-item d-none d-md-flex" ref={exploreRef}>
            <div className="dropdown">
              <button
                type="button"
                className="topbar-link btn fw-medium btn-link dropdown-toggle drop-arrow-none px-2"
                onClick={() => setExploreOpen((open) => !open)}
              >
                Explore
                <i className="ti ti-chevron-down ms-1" />
              </button>

              <div
                className={`dropdown-menu dropdown-menu-xxl p-0${exploreOpen ? " show" : ""}`}
                style={{ position: "absolute", top: "100%", left: 0, marginTop: "0.5rem" }}
              >
                <div style={{ maxHeight: 380, overflowY: "auto" }}>
                  <div className="row g-0">
                    <div className="col-sm-8">
                      <div className="row g-0">
                        <div className="col-sm-6">
                          <div className="p-2">
                            <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent" onClick={() => goExplore(ROUTES.DASHBOARD)}>
                              <span className="d-flex justify-content-start">
                                <span className="avatar-md me-2">
                                  <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                    <i className="ti ti-gauge fs-22" />
                                  </span>
                                </span>
                                <span>
                                  <h5 className="fs-base mb-0 lh-base">Dashboards</h5>
                                  <span className="text-muted fs-12">Overview &amp; Analytics</span>
                                </span>
                              </span>
                            </button>

                            <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent my-2" onClick={() => goExplore(ROUTES.PROJECT)}>
                              <span className="d-flex justify-content-start">
                                <span className="avatar-md me-2">
                                  <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                    <i className="ti ti-pencil fs-22" />
                                  </span>
                                </span>
                                <span>
                                  <h5 className="fs-base mb-0 lh-base">Project</h5>
                                  <span className="text-muted fs-12">Manage Projects</span>
                                </span>
                              </span>
                            </button>

                            {/* Cost Estimation / Rate Card / Master / Account /
                                Configuration tiles below are each hidden per the
                                Role Matrix, same gating as SideMenu.jsx -- see
                                src/utils/permissions.js for the tier table. */}
                            {isModuleVisible("CostEstimation") && (
                              <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent my-2" onClick={goStartNewEstimation}>
                                <span className="d-flex justify-content-start">
                                  <span className="avatar-md me-2">
                                    <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                      <i className="ti ti-calculator fs-22" />
                                    </span>
                                  </span>
                                  <span>
                                    <h5 className="fs-base mb-0 lh-base">Cost Estimation</h5>
                                    <span className="text-muted fs-12">Calculate Costs</span>
                                  </span>
                                </span>
                              </button>
                            )}

                            {isModuleVisible("RateCard") && (
                              <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent mt-2" onClick={() => goExplore(ROUTES.RATE_CARD)}>
                                <span className="d-flex justify-content-start">
                                  <span className="avatar-md me-2">
                                    <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                      <img
                                        src="/assets/images/logo/riyal-symbol-green.png"
                                        alt="Rate Card"
                                        style={{ height: "22px", width: "auto" }}
                                      />
                                    </span>
                                  </span>
                                  <span>
                                    <h5 className="fs-base mb-0 lh-base">Rate Card</h5>
                                    <span className="text-muted fs-12">Pricing &amp; Rates</span>
                                  </span>
                                </span>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="col-sm-6">
                          <div className="p-2">
                            {/* Stale link fixed: this used to point at
                                "/account/roles" (the old Master submenu's
                                first page, from before Role/Department/
                                Company were pulled out from under Master).
                                SideMenu.jsx's own Master link was updated to
                                go straight to "/master" (the Coming Soon
                                placeholder) when that submenu was removed,
                                but this Explore mega-menu tile was never
                                updated to match -- it was still sending
                                people to the old page. Now matches
                                SideMenu.jsx. */}
                            {isModuleVisible("Master") && (
                              <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent" onClick={() => goExplore(ROUTES.MASTER)}>
                                <span className="d-flex justify-content-start">
                                  <span className="avatar-md me-2">
                                    <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                      <i className="ti ti-database fs-22" />
                                    </span>
                                  </span>
                                  <span>
                                    <h5 className="fs-base mb-0 lh-base">Master</h5>
                                    <span className="text-muted fs-12">Master Data Management</span>
                                  </span>
                                </span>
                              </button>
                            )}

                            {isModuleVisible("AccountUsers") && (
                              <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent my-2" onClick={() => goExplore(ROUTES.ACCOUNT_USERS)}>
                                <span className="d-flex justify-content-start">
                                  <span className="avatar-md me-2">
                                    <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                      <i className="ti ti-users fs-22" />
                                    </span>
                                  </span>
                                  <span>
                                    <h5 className="fs-base mb-0 lh-base">Account</h5>
                                    <span className="text-muted fs-12">User Profiles &amp; Roles</span>
                                  </span>
                                </span>
                              </button>
                            )}

                            {isModuleVisible("Configuration") && (
                              <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent my-2" onClick={() => goExplore(ROUTES.CONFIG_MAIL)}>
                                <span className="d-flex justify-content-start">
                                  <span className="avatar-md me-2">
                                    <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                      <i className="ti ti-settings fs-22" />
                                    </span>
                                  </span>
                                  <span>
                                    <h5 className="fs-base mb-0 lh-base">Configuration</h5>
                                    <span className="text-muted fs-12">System Settings</span>
                                  </span>
                                </span>
                              </button>
                            )}

                            <button type="button" className="dropdown-item text-start w-100 border-0 bg-transparent mt-2" onClick={() => goExplore(ROUTES.HELP)}>
                              <span className="d-flex justify-content-start">
                                <span className="avatar-md me-2">
                                  <span className="avatar-title text-success border border-light bg-light bg-opacity-50 rounded">
                                    <i className="ti ti-help-circle fs-22" />
                                  </span>
                                </span>
                                <span>
                                  <h5 className="fs-base mb-0 lh-base">Help</h5>
                                  <span className="text-muted fs-12">Support &amp; Documentation</span>
                                </span>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Support/Helpline strip -- per request, pulled from
                          Contact_Info (same source as Help.jsx's "Get in
                          Touch" panel) instead of the hardcoded
                          info@dashboard.com / +966-123456789 this used to
                          show. Only the first email/number is shown (this
                          strip only has room for one of each); falls back
                          to those old hardcoded values if the fetch above
                          hasn't resolved yet or fails. */}
                      <div className="row g-0 border-top border-light border-dashed text-center">
                        <div className="col">
                          <div className="p-3">
                            <p className="fw-medium text-muted mb-2 fs-11 text-uppercase lh-1">-: &nbsp; Support &nbsp;:-</p>
                            <h5 className="fs-15 mb-0">
                              <a href={`mailto:${supportEmail}`} className="text-dark">{supportEmail}</a>
                            </h5>
                          </div>
                        </div>
                        <div className="col">
                          <div className="p-3">
                            <p className="fw-medium text-muted mb-2 fs-11 text-uppercase lh-1">-: &nbsp; Helpline &nbsp;:-</p>
                            <h5 className="fs-15 mb-0">
                              {/* tel: href needs digits/+ only -- strip any
                                  dashes/spaces Contact_Info's ContactNumbers
                                  value has for display (e.g. "+966-1234567890"). */}
                              <a href={`tel:${helplineNumber.replace(/[^+\d]/g, "")}`} className="text-dark">{helplineNumber}</a>
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-4">
                      <div
                        className="h-100 position-relative rounded-end rounded-0 overflow-hidden"
                        style={{ background: "url(/assets/images/new/main-menu.jpg)", backgroundSize: "cover" }}
                      >
                        <div className="p-3 card-img-overlay bg-gradient bg-secondary bg-opacity-90 d-flex align-items-center justify-content-center">
                          <div className="text-center text-white">
                            <img style={{ width: 50, marginBottom: 10 }} src="/assets/images/logo/CET-Full-icon.png" alt="" />
                            <p className="text-white mb-3 text-uppercase">Cost Estimation</p>
                            <h3 className="fw-semibold text-white mb-2 fs-20">Accurate Estimates. Controlled Costs. Better Projects.</h3>
                            <button type="button" className="btn btn-warning btn-sm mt-3" onClick={goStartNewEstimation}>
                              Start Estimation
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Notification bell */}
          <div className="topbar-item" ref={notifRef}>
            <div className="dropdown">
              <button
                type="button"
                className="topbar-link dropdown-toggle drop-arrow-none btn btn-icon position-relative"
                onClick={() => setNotifOpen((open) => !open)}
              >
                <i className="ti ti-bell fs-22" />
                {unreadCount > 0 && (
                  // Was "top-0 start-100 translate-middle" (Bootstrap's
                  // usual corner-straddling badge -- half the badge sits
                  // ABOVE the button's own top edge). Something in the
                  // topbar clips content that pokes above it (a fixed-
                  // height sticky bar, most likely -- app.min.css isn't
                  // present locally to confirm which rule), so only a
                  // sliver of the badge showed. Keeping the whole badge
                  // inside the button's own box (top/right offsets, no
                  // translate) sidesteps that clipping regardless of its
                  // exact cause.
                  <span
                    className="position-absolute badge rounded-pill bg-danger"
                    style={{ top: 2, right: 2 }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>

              <div
                className={`dropdown-menu dropdown-menu-end dropdown-menu-lg p-0${notifOpen ? " show" : ""}`}
                style={{ position: "absolute", top: "100%", right: 0, left: "auto", marginTop: "0.5rem" }}
              >
                <div className="px-3 py-2 border-bottom">
                  <h6 className="m-0 fs-md fw-semibold">Notifications</h6>
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {/* Per request, the category sections (COST ESTIMATION /
                      ACCESS REQUEST / ...) were removed -- they fixed each
                      category to its own spot regardless of how recent its
                      notifications actually were, so a brand new one (e.g.
                      a just-submitted Help message) could end up buried
                      near the bottom under an older category instead of
                      showing at the top. Now it's one flat list, newest
                      first overall (the notifications array itself already
                      comes back from the API newest-first). */}
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`dropdown-item text-wrap py-2${n.status === "Unread" ? " fw-semibold" : ""}`}
                      >
                        {/* Per request: actor name + date as a header
                            line above the message, message itself
                            prefixed with a per-type icon -- see
                            getNotificationIcon/formatNotificationDate
                            above. actorName is only present for rows
                            written after notifications_actor_name_
                            column.sql was added, so an older row just
                            shows its message with no header line. */}
                        {/* Per request: "Name - date" joined with a
                            literal hyphen on one line (not spread
                            apart), icon + message on the line below. */}
                        {n.actorName && (
                          <div className="text-muted fs-12">
                            {n.actorName} - {formatNotificationDate(n.createdAt)}
                          </div>
                        )}
                        <div>
                          {isCostEstimationNotification(n) ? (
                            <NotificationAvatar actorName={n.actorName} actorProfileImage={n.actorProfileImage} />
                          ) : (
                            getNotificationIcon(n)
                          )}{" "}
                          {n.message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item text-wrap py-2 text-muted">No new notifications.</div>
                  )}
                </div>
                <div className="text-center py-2 border-top">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => {
                      setNotifOpen(false);
                      navigate(ROUTES.NOTIFICATIONS);
                    }}
                  >
                    View all
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* User profile */}
          <div className="dropdown topbar-item" ref={userMenuRef}>
            <button
              type="button"
              className="topbar-link dropdown-toggle drop-arrow-none btn btn-link text-decoration-none d-flex align-items-center"
              onClick={() => setUserMenuOpen((open) => !open)}
            >
              {userAvatarUrl ? (
                <img src={userAvatarUrl} alt={userName} className="rounded-circle avatar-sm" />
              ) : (
                <span className="rounded-circle avatar-sm d-flex align-items-center justify-content-center bg-primary text-white fw-semibold">
                  {initials}
                </span>
              )}
              <span className="d-none d-md-block ms-2 text-start">
                <h5 className="my-0 fs-14">{userName}</h5>
                {userRole && <p className="mb-0 fs-12 text-muted">{userRole}</p>}
              </span>
            </button>

            <div
              className={`dropdown-menu dropdown-menu-end${userMenuOpen ? " show" : ""}`}
              style={{ position: "absolute", top: "100%", right: 0, left: "auto", marginTop: "0.5rem" }}
            >
              <div className="dropdown-header noti-title">
                <h6 className="text-overflow m-0">Welcome back!</h6>
              </div>

              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate(ROUTES.PROFILE);
                }}
              >
                <i className="ti ti-user-circle me-1 fs-lg align-middle" />
                <span className="align-middle">Profile</span>
              </button>

              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate(ROUTES.NOTIFICATIONS);
                }}
              >
                <i className="ti ti-bell me-1 fs-lg align-middle" />
                <span className="align-middle">Notifications</span>
              </button>

              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate(ROUTES.CHANGE_PASSWORD);
                }}
              >
                <i className="ti ti-lock me-1 fs-lg align-middle" />
                <span className="align-middle">Change Password</span>
              </button>

              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate(ROUTES.HELP);
                }}
              >
                <i className="ti ti-headset me-1 fs-lg align-middle" />
                <span className="align-middle">Support Center</span>
              </button>

              <div className="dropdown-divider" />

              <button
                type="button"
                className="dropdown-item text-danger fw-semibold"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/signout");
                }}
              >
                <i className="ti ti-logout me-1 fs-lg align-middle" />
                <span className="align-middle">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
